import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { visit } from 'unist-util-visit';
import type { Root, Text, PhrasingContent } from 'mdast';
import 'katex/dist/katex.min.css';
import { Api } from '../../lib/api';
import { splitRaw, readFrontmatter } from '../../lib/frontmatter';

const IMG_EXT = /\.(png|jpe?g|gif|webp|svg)$/i;
const WIKILINK = /(!?)\[\[([^\[\]]+?)\]\]/g;

/** remark plugin: turn [[wikilinks]] and ![[embeds]] into link/image nodes. */
export function remarkWikilinks() {
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || index === undefined) return;
      const text = node.value;
      if (!text.includes('[[')) return;
      const out: PhrasingContent[] = [];
      let last = 0;
      WIKILINK.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = WIKILINK.exec(text)) !== null) {
        if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
        const [full, bang, inner] = m;
        const [targetPart, alias] = inner.split('|');
        const target = targetPart.trim();
        if (bang === '!' && IMG_EXT.test(target)) {
          out.push({ type: 'image', url: Api.attachments.url(target), alt: alias ?? target });
        } else {
          out.push({
            type: 'link',
            url: `wikilink:${encodeURIComponent(target)}`,
            data: { hProperties: { className: bang === '!' ? ['wikilink', 'embed'] : ['wikilink'] } },
            children: [{ type: 'text', value: (alias ?? target).trim() }],
          });
        }
        last = m.index + full.length;
      }
      if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
      (parent.children as PhrasingContent[]).splice(index, 1, ...out);
      return index + out.length;
    });
  };
}

let mermaidReady: Promise<typeof import('mermaid')['default']> | null = null;
function loadMermaid() {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((m) => {
      const css = getComputedStyle(document.documentElement);
      m.default.initialize({
        startOnLoad: false,
        theme: 'base',
        securityLevel: 'strict',
        fontFamily: css.getPropertyValue('--font-ui'),
        themeVariables: {
          background: css.getPropertyValue('--color-surface').trim() || '#171923',
          primaryColor: '#1e2130',
          primaryTextColor: '#eceef5',
          primaryBorderColor: '#5b9cf8',
          lineColor: '#5b9cf8',
          secondaryColor: '#171923',
          tertiaryColor: '#0d0f18',
          fontSize: '13px',
        },
      });
      return m.default;
    });
  }
  return mermaidReady;
}

let mermaidSeq = 0;
function Mermaid({ chart }: { chart: string }) {
  const [svg, setSvg] = useState<string>('');
  const [err, setErr] = useState<string>('');
  useEffect(() => {
    let alive = true;
    loadMermaid()
      .then((m) => m.render(`cortex-mmd-${++mermaidSeq}`, chart))
      .then((r) => alive && (setSvg(r.svg), setErr('')))
      .catch((e) => alive && setErr(String((e as Error).message ?? e)));
    return () => {
      alive = false;
    };
  }, [chart]);
  if (err) return <pre className="mermaid-block mermaid-error">{err}</pre>;
  return <div className="mermaid-block" dangerouslySetInnerHTML={{ __html: svg }} />;
}

const slug = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');

export interface PreviewProps {
  raw: string;
  onOpenLink: (target: string, opts: { split?: boolean }) => void;
  noteId?: string;
}

export function Preview({ raw, onOpenLink, noteId }: PreviewProps) {
  const { body } = useMemo(() => splitRaw(raw), [raw]);
  const fm = useMemo(() => readFrontmatter(raw), [raw]);
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onGoto = (e: Event) => {
      const d = (e as CustomEvent).detail as { noteId: string; heading: string };
      if (d.noteId !== noteId) return;
      host.current?.querySelector<HTMLElement>(`#${CSS.escape(slug(d.heading))}`)?.scrollIntoView({ block: 'start' });
    };
    document.addEventListener('cortex:goto-heading', onGoto);
    return () => document.removeEventListener('cortex:goto-heading', onGoto);
  }, [noteId]);

  const fmEntries = Object.entries(fm).filter(([k]) => k !== 'title');

  return (
    <div className="preview" ref={host}>
      <div className="inner">
        {fmEntries.length > 0 && (
          <div className="fm-block">
            {fmEntries.map(([k, v]) => (
              <Fragment key={k}>
                <span className="k">{k}</span>
                <span>{typeof v === 'string' ? v : JSON.stringify(v)}</span>
              </Fragment>
            ))}
          </div>
        )}
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath, remarkWikilinks]}
          rehypePlugins={[rehypeKatex]}
          components={{
            a: ({ href, children, className, ...rest }) => {
              if (href?.startsWith('wikilink:')) {
                const target = decodeURIComponent(href.slice('wikilink:'.length));
                return (
                  <a
                    {...rest}
                    className={className as string}
                    href={`#/note/${encodeURIComponent(target)}`}
                    title={target}
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenLink(target, { split: e.shiftKey || e.ctrlKey || e.metaKey });
                    }}
                  >
                    {children}
                  </a>
                );
              }
              return (
                <a {...rest} href={href} target="_blank" rel="noreferrer noopener">
                  {children}
                </a>
              );
            },
            code: ({ className, children, ...rest }) => {
              const lang = /language-(\w+)/.exec(className ?? '')?.[1];
              if (lang === 'mermaid') return <Mermaid chart={String(children).replace(/\n$/, '')} />;
              return (
                <code className={className} {...rest}>
                  {children}
                </code>
              );
            },
            h1: ({ children }) => <h1 id={slug(String(children))}>{children}</h1>,
            h2: ({ children }) => <h2 id={slug(String(children))}>{children}</h2>,
            h3: ({ children }) => <h3 id={slug(String(children))}>{children}</h3>,
            h4: ({ children }) => <h4 id={slug(String(children))}>{children}</h4>,
            input: ({ checked, ...rest }) => <input type="checkbox" checked={!!checked} readOnly {...rest} />,
          }}
        >
          {body}
        </ReactMarkdown>
      </div>
    </div>
  );
}
